import React, { useEffect, useMemo } from 'react'
import _ from 'lodash'
import type { Element } from '@xmldom/xmldom'
import L1ErrorBlock from '../l1/L1ErrorBlock'
import XFlowTags from './XFlowTags'
import { getTypedXmlAttributes, xmlNodeIsElement } from '@fusion-advantage/backcompat'
import { XPathCalc } from '@fusion-advantage/formlib'
import { useFormDispatch, useFormSelector } from '../../../redux/formStoreHooks'
import { selectForm } from '../../../redux/selectForm'
import { selectMajorRenderErrors } from '../../../redux/selectMajorRenderErrors'
import { incrementRendererLoadSerial } from '../../../redux/formSlice'

const L5XMajorRenderErrors = () => {
	const majorRenderErrors = useFormSelector(selectMajorRenderErrors)
	if(majorRenderErrors.length > 0) {
		return <L1ErrorBlock bullets={majorRenderErrors} />
	}
	return null
}

interface L5XFlowRendererProps {
	pageIdx?: number|null,
}

export const L5XFlowRenderer = React.memo(({pageIdx}: L5XFlowRendererProps) => {
	const { rootNode } = useFormSelector(selectForm)
	const dispatch = useFormDispatch()
	useEffect(() => {
		dispatch(incrementRendererLoadSerial())
	}, [dispatch])
	const contentNode = useMemo(() => {
		if(!rootNode) {
			return null
		}
		if(!_.isNumber(pageIdx)) {
			return rootNode
		}
		let pageNode
		if(rootNode.hasChildNodes()) {
			let childPageIdx = -1
			for(let nodeIdx in rootNode.childNodes) {
				let node = rootNode.childNodes[nodeIdx]
				if(!xmlNodeIsElement(node)) {
					continue
				}
				if(node.nodeName === 'Page') {
					childPageIdx++
					if(childPageIdx === pageIdx) {
						pageNode = node
						break
					}
				}
			}
		}
		return pageNode
	}, [rootNode, pageIdx])
	if(!contentNode) {
		return <L1ErrorBlock content={`[L5XFlowRenderer] rootNode or pageIdx ${pageIdx} not found`} />
	}
	let xpath = `${rootNode?.nodeName}`
	if(_.isNumber(pageIdx)) {
		xpath += `.${contentNode.nodeName}[${pageIdx}]`
	}
	return (<>
		<L5XMajorRenderErrors />
		<L5XFlowNodeHydrator
			node={contentNode}
			xpath={xpath}
		/>
	</>)
})

interface L5XFlowNodeHydratorProps {
	node: Element,
	xpath: string,
}

const L5XFlowNodeHydrator = React.memo(({node, xpath}: L5XFlowNodeHydratorProps) => {
	if(!xmlNodeIsElement(node)) {
		return <L1ErrorBlock content={`[${xpath}] node not instance of Element`} />
	}
	const children = useMemo(() => {
		const parentNode = node
		const children = []
		const xpathcalc = new XPathCalc(xpath)
		for(let nodeIdx in parentNode.childNodes) {
			let node = parentNode.childNodes[nodeIdx]
			if(!xmlNodeIsElement(node)) {
				continue
			}
			let { nodeName } = node
			let nodeXPath = xpathcalc.getNext(nodeName)
			children.push(
				<L5XFlowNodeHydrator
					key={nodeXPath}
					xpath={nodeXPath}
					node={node}
				/>
			)
		}
		return children
	}, [node, xpath])
	const props = useMemo(() => {
		return {
			xpath,
			children,
			...getTypedXmlAttributes(node),
		}
	}, [node, xpath, children])
	if('error' in props) {
		return <L1ErrorBlock title={String(props.error)} />
	}
	const Component = XFlowTags[node.nodeName]
	if(!Component) {
		return <L1ErrorBlock title={`[${xpath}] undefined tag ${node.nodeName}`} />
	}
	return <Component {...props} />
})

